import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Paper, Stack, IconButton, Typography } from '@mui/material';
import { MachineIcon } from 'web-components';
import { getSensorProps } from 'helpers/utils';
import { SENSOR_STATUS_OK } from 'attrs/notifications';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import ModeEditOutlinedIcon from '@mui/icons-material/ModeEditOutlined';
import { getMillSensor } from 'attrs/sensorType';
import { useTranslation } from 'react-i18next';
import { getLineStyle } from 'attrs/chartsTemplate';
import EditSensorCard from './EditSensorCard';
import DeleteSensorCard from './DeleteSensorCard';

const truncatedTextSx = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  width: '100%'
};

function CarouselCard({ sensorIdx, sensor, handleEditCardSensor, handleDeleteSensor }) {
  const { t } = useTranslation();
  const millSensor = getMillSensor(sensor?.type);
  const name = t(millSensor?.name || sensor?.name);
  const mapValue = millSensor?.mapValue || sensor?.custom_icon_type || sensor?.static_icon_type || 'n/a';
  const isNotStatic = !sensor?.is_static;
  const [openEditSensorDialog, setOpenEditSensorDialog] = useState(false);
  const [openDeleteSensorDialog, setOpenDeleteSensorDialog] = useState(false);

  const handleOpenEditSensorDialog = () => {
    setOpenEditSensorDialog(true);
  };

  const handleCloseEditSensorDialog = () => {
    setOpenEditSensorDialog(false);
  };

  const handleOpenDeleteSensorDialog = () => {
    setOpenDeleteSensorDialog(true);
  };

  const handleCloseDeleteSensorDialog = () => {
    setOpenDeleteSensorDialog(false);
  };

  const handleDeleteCard = () => {
    handleDeleteSensor(sensor?.temp_id || sensor?.id);
  };

  const machineIconProps = {
    iconName: getSensorProps(mapValue).icon,
    // accepted status type: SENSOR_STATUS_NODATA, SENSOR_STATUS_OK, SENSOR_STATUS_WARNING, SENSOR_STATUS_CRITICAL
    status: SENSOR_STATUS_OK,
    style: {
      fontSize: '2rem'
    },
    viewBox: '0 0 32 32'
  };

  const notStaticBorder = `${sensor?.line_thickness || 1}px ${getLineStyle(sensor?.line_style)}`;
  const staticBorder = '2px solid';
  return (
    <Paper
      sx={{
        width: '100%',
        minWidth: 300,
        minHeight: 96,
        maxHeight: 96,
        border: isNotStatic ? notStaticBorder : staticBorder,
        borderColor: isNotStatic ? sensor?.line_color : '#999999',
        borderRadius: 2,
        p: 1,
        overflow: 'hidden'
      }}
      data-testid="carousel-card"
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ height: '100%' }}>
        <Stack
          direction="column"
          justifyContent="space-around"
          alignItems="flex-start"
          spacing={0.5}
          sx={{ flex: 1, minWidth: 0 }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={1}
            sx={{ width: '100%', minWidth: 0 }}
          >
            <MachineIcon {...machineIconProps} />
            <Typography sx={{ fontSize: 14, fontWeight: 'bold', flex: 1, minWidth: 0, ...truncatedTextSx }}>
              {t(`${name}`)}
            </Typography>
          </Stack>
          <Typography sx={{ fontSize: 10, ...truncatedTextSx }}>{sensor?.production_line}</Typography>
          <Typography sx={{ fontSize: 10, ...truncatedTextSx }}>{sensor?.machine_name}</Typography>
        </Stack>
        <Stack direction="column" justifyContent="space-between" alignItems="center" spacing={0}>
          <IconButton
            data-testid="edit-card-sensor-button"
            aria-label="edit"
            onClick={handleOpenEditSensorDialog}
            sx={{
              visibility: isNotStatic ? 'visible' : 'hidden'
            }}
          >
            <ModeEditOutlinedIcon color="primary" fontSize="inherit" />
          </IconButton>
          <IconButton
            data-testid="delete-card-sensor-button"
            aria-label="delete"
            onClick={handleOpenDeleteSensorDialog}
          >
            <DeleteForeverOutlinedIcon sx={{ color: '#C12907' }} fontSize="inherit" />
          </IconButton>
        </Stack>
      </Stack>
      <EditSensorCard
        open={openEditSensorDialog}
        sensorIdx={sensorIdx}
        sensor={sensor}
        handleCloseDialog={handleCloseEditSensorDialog}
        handleApply={handleEditCardSensor}
      />
      <DeleteSensorCard
        open={openDeleteSensorDialog}
        handleCloseDialog={handleCloseDeleteSensorDialog}
        handleApply={handleDeleteCard}
      />
    </Paper>
  );
}

CarouselCard.propTypes = {
  sensorIdx: PropTypes.number.isRequired,
  sensor: PropTypes.shape().isRequired,
  handleEditCardSensor: PropTypes.func.isRequired,
  handleDeleteSensor: PropTypes.func.isRequired
};

export default CarouselCard;
